import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'
import { logger } from '@/utils/logger'

/**
 * トップ画面表示用情報取得
 */
import { CoursePlanData } from './ContractCoursePlan'
import { ApiTaTp010 } from '~/endpoints/ApiTaTp010'

/**
 * 教科ID
 */
export const curriculumType = {
  None: '',
  Kokugo: 'L2', // 国語
  Sansu: 'M2', // 算数
  Rika: 'P2', // 理科
  Shakai: 'J2', // 社会
  Eigo: 'E2', // 英語
  Mirai: '72', // 未来探究学習
} as const
export type CurriculumType = typeof curriculumType[keyof typeof curriculumType]

/**
 * バッジフラグ種別
 */
export const badgeType = {
  None: '',
  Hidden: '0', // 非表示
  Hanamaru: '1', // はなまる
  LateLarge: '2', // 遅れ大
  LateNormal: '3', // 遅れ
  Normal: '4', // 無印
} as const
export type BadgeType = typeof badgeType[keyof typeof badgeType]

/**
 * カリキュラムデータ
 */
export interface CurriculumData {
  unitsId: string
  curriculumType: CurriculumType
  boldFlg: boolean
  badgeType: BadgeType
  lessonId: string
  pieceType: string
}

// State types
export interface StateTopContent {
  curriculums: Array<CurriculumData>
  statusCode: number
  error: {
    messageId: string
    message: string
  } | null
}
export const getEmptyStateTopContent = (): StateTopContent => ({
  curriculums: [],
  statusCode: 0,
  error: null,
})

@Module({
  stateFactory: true,
  namespaced: true,
  name: 'apis/TopContent',
})
export default class TopContent extends VuexModule {
  // States
  private state_: StateTopContent = getEmptyStateTopContent()

  // Getters

  get result(): StateTopContent {
    return this.state_
  }

  get curriculums(): Array<CurriculumData> {
    return this.state_.curriculums
  }

  // Mutations

  @Mutation
  updateTopContent(value: StateTopContent) {
    this.state_ = value
  }

  static readonly MUTATION_NAME_UPDATE_STATE = 'updateTopContent'

  // Actions

  @Action
  clear() {
    this.context.commit(TopContent.MUTATION_NAME_UPDATE_STATE, getEmptyStateTopContent())
  }

  @Action
  async getTopContent(data: CoursePlanData) {
    logger.trace('in: getTopContent : ' + data.courseId + ', ' + data.planId)
    const api = new ApiTaTp010.Api()

    // API
    const result = await api.get<StateTopContent>(
      {
        query: data,
      },
      // Success
      (data, status) => {
        return {
          curriculums: TopContent.convertCurriculumData(data.curriculums),
          statusCode: status,
          error: null,
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        return {
          curriculums: getEmptyStateTopContent().curriculums,
          statusCode: status,
          error: {
            messageId: data.error.messageId,
            message: data.error.message,
          },
        }
      }
    )

    const commitdata: StateTopContent = getEmptyStateTopContent()
    if (result) {
      commitdata.statusCode = result.statusCode
      commitdata.error = result.error
      if (commitdata.error === null) {
        commitdata.curriculums = result.curriculums
      }
    }
    this.context.commit(TopContent.MUTATION_NAME_UPDATE_STATE, commitdata)
  }

  static convertCurriculumData(arrayIn: Array<ApiTaTp010.Get.Curriculum>): CurriculumData[] {
    const arrayRet: CurriculumData[] = []
    arrayIn.forEach(elm => {
      const tmp = {
        unitsId: elm.unitsId,
        curriculumType: elm.curriculumId as CurriculumType,
        boldFlg: elm.boldFlg === '1',
        badgeType: elm.badgeFlg as BadgeType,
        lessonId: elm.lessonId,
        pieceType: elm.pieceType,
      } as CurriculumData
      arrayRet.push(tmp)
    })
    return arrayRet
  }
}
